import api from './api';

export interface CalendarItem {
  source: 'event' | 'training' | 'fixture';
  id: string;
  title: string;
  type: 'training' | 'match' | 'medical' | 'meeting' | 'travel' | 'other';
  start: string;
  end?: string;
  meetAt?: string;
  location?: string;
  description?: string;
  editable: boolean;
  visibility: 'normal' | 'medical';
  attachments?: { id: string; fileName: string }[];
  createdBy?: string;
  seriesId?: string;
}

export interface CreateEventPayload {
  title: string;
  type: string;
  startsAt: string;
  endsAt?: string;
  allDay?: boolean;
  meetAt?: string;
  location?: string;
  description?: string;
  visibility?: string;
  userIds?: string[];
  groupIds?: string[];
  wholeSquad?: boolean;
  recurrence?: {
    freq: 'daily' | 'weekly' | 'fortnightly' | 'monthly';
    endMode: 'date' | 'count';
    until?: string; // 'YYYY-MM-DD' when endMode === 'date'
    count?: number; // when endMode === 'count'
  };
  setAssignees?: boolean;
}

export const calendarAPI = {
  myFeed: async (from?: string, to?: string): Promise<{ data: CalendarItem[] }> => {
    const sp = new URLSearchParams();
    if (from) sp.append('from', from);
    if (to) sp.append('to', to);
    const r = await api.get(`/calendar/my${sp.toString() ? `?${sp}` : ''}`);
    return r.data;
  },

  feed: async (params: { from?: string; to?: string; userId?: string }): Promise<{ data: CalendarItem[] }> => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && sp.append(k, v));
    const r = await api.get(`/calendar/feed${sp.toString() ? `?${sp}` : ''}`);
    return r.data;
  },

  createEvent: async (p: CreateEventPayload) => (await api.post('/calendar/events', p)).data,

  createPersonal: async (p: CreateEventPayload) => (await api.post('/calendar/my/events', p)).data,

  updateEvent: async (id: string, p: CreateEventPayload, opts?: { scope?: 'single' | 'series' }) =>
    (await api.put(`/calendar/events/${id}${opts?.scope === 'series' ? '?scope=series' : ''}`, p)).data,

  deleteEvent: async (id: string, opts?: { scope?: 'single' | 'series' }) =>
    (await api.delete(`/calendar/events/${id}${opts?.scope === 'series' ? '?scope=series' : ''}`)).data,

  assign: async (
    id: string,
    body: { targetType: string; userIds?: string[]; groupIds?: string[]; wholeSquad?: boolean }
  ) => (await api.post(`/calendar/events/${id}/assignments`, body)).data,

  assignableUsers: async () => (await api.get('/calendar/assignable-users')).data,

  eventAssignees: async (id: string): Promise<{ data: string[] }> =>
    (await api.get(`/calendar/events/${id}/assignments`)).data,

  groups: async () => (await api.get('/calendar/groups')).data,

  uploadAttachment: async (eventId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post(`/calendar/events/${eventId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  downloadAttachment: async (attachmentId: string): Promise<Blob> =>
    (await api.get(`/calendar/attachments/${attachmentId}`, { responseType: 'blob' })).data,

  deletePersonal: async (id: string) => (await api.delete(`/calendar/my/events/${id}`)).data,
};
